import { useEffect, useState } from "react";
import { Button } from "../../../components/ui/button";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
  DialogTrigger,
} from "../../../components/ui/dialog";
import {
  Select,
  SelectTrigger,
  SelectValue,
  SelectContent,
  SelectGroup,
  SelectItem,
} from "../../../components/ui/select";
import { Label } from "../../../components/ui/label";
import { Input } from "../../../components/ui/input";
import { Checkbox } from "../../../components/ui/checkbox";
import { PencilIcon, Eye, EyeOff } from "lucide-react";
import apiClient from "../../../utils/apiClient";
import toast from "react-hot-toast";
import server from "../../../utils/serverConfig";

const statusOptions = [
  { value: "active", label: "active" },
  { value: "inactive", label: "inactive" },
  { value: "deactivated", label: "deactivated" },
  { value: "dummy", label: "dummy" },
];

export function EditDhanXTSUserModal({ userDetails, fetchFyersUserList, forceOpen = false, onForceClose }) {
  const [open, setOpen] = useState(false);
  const [showCustomLabelInput, setShowCustomLabelInput] = useState(false);

  // Single state object for all input fields
  const [userData, setUserData] = useState({
    clientId: "",
    password: "",
    username: "",
    status: "",
    apiAppKey: "",
    apiAppSecret: "",
    sessionToken: "",
    label: "",
    type: "",
    product: "optimus",
  });

  const [visibleFields, setVisibleFields] = useState({});

  const toggleVisibility = (field) => {
    setVisibleFields((prev) => ({ ...prev, [field]: !prev[field] }));
  };

  // Fetch user details on component load
  const getDecryptedUserDetails = async () => {
    const data = {
      client_id: userDetails.client_id,
      broker: userDetails.broker,
    };

    const config = {
      method: "post",
      url: `${server.godseye.baseUrl}get-dhan-xts-user-by-id`,
      headers: {
        "Content-Type": "application/json",
      },
      data: JSON.stringify(data),
    };

    try {
      const response = await apiClient.request(config);

      // Populate the form with the fetched data
      setUserData({
        clientId: response.data.client_id || "",
        password: response.data.password || "",
        username: response.data.username || "",
        status: response.data.status || "",
        apiAppKey: response.data.api_app_key || "",
        apiAppSecret: response.data.api_app_secret || "",
        sessionToken: response.data.session_token || "",
        label: response.data.label || "",
        type: response.data.type || "",
        product: response.data.product || "optimus",
      });

      // Check if we need to show custom input
      const predefinedLabels = ["clearmind", "satyam"];
      setShowCustomLabelInput(!predefinedLabels.includes(response.data.label || "") && response.data.label !== "");
    } catch (error) {
      console.error("Failed to get user details:", error);
    }
  };

  // Common handleChange function
  const handleChange = (e) => {
    const { name, value } = e.target;
    setUserData((prev) => ({ ...prev, [name]: value }));
  };

  const handleUpdateUser = () => {
    const data = {
      client_id: userData.clientId,
      password: userData.password,
      username: userData.username,
      status: userData.status,
      api_app_key: userData.apiAppKey,
      api_app_secret: userData.apiAppSecret,
      label: userData.label,
      type: userData.type,
      product: userData.product || "optimus",
      broker: userDetails.broker, // "dhan_xts" or "dhan_xts_anurag"
    };

    console.log("Updated Dhan XTS User Data", data);

    let config = {
      method: "put",
      url: `${server.godseye.baseUrl}edit-dhan-xts-user`,
      headers: {
        "Content-Type": "application/json",
      },
      data: JSON.stringify(data),
    };

    apiClient
      .request(config)
      .then((response) => {
        console.log("Server response:", response.data);
        toast.success("Dhan XTS user updated successfully!", {
          duration: 5000,
        });
      })
      .catch((error) => {
        console.error("Failed to update user", error);
        toast.error("Failed to update user", {
          duration: 5000,
        });
      })
      .finally(() => {
        setOpen(false);
        fetchFyersUserList();
      });
  };

  const handleOpenModal = () => {
    setOpen(true);
    getDecryptedUserDetails();
  };

  useEffect(() => {
    if (forceOpen) {
      handleOpenModal();
    }
  }, [forceOpen]);

  return (
    <Dialog open={open} onOpenChange={(val) => { setOpen(val); if (!val && onForceClose) onForceClose(); }}>
      {!forceOpen && (
        <DialogTrigger asChild>
          <Button
            onClick={handleOpenModal}
            variant="outline"
            className="text-[12px] h-[25px] font-medium bg-zinc-800 text-white hover:bg-zinc-700"
          >
            <PencilIcon className="mr-2 w-3 h-3 text-gray-500" />
            Edit User
          </Button>
        </DialogTrigger>
      )}
      <DialogContent className="sm:max-w-[1025px] border-zinc-700">
        <DialogHeader>
          <DialogTitle className="text-white">
            Edit Dhan XTS User Details ({userDetails.broker})
          </DialogTitle>
        </DialogHeader>
        <div className="grid grid-cols-3 gap-4 py-4">
          {Object.keys(userData)
            .filter((field) => !["status", "label"].includes(field))
            .map((field) => (
              <div key={field} className="grid grid-cols-1 items-center gap-4">
                <Label className="text-white">
                  {field.replace(/([A-Z])/g, " $1")}
                </Label>
                <div className="relative">
                  <Input
                    className="text-white pr-10"
                    type={
                      (field.toLowerCase().includes("password") || 
                       field.toLowerCase().includes("secret") || 
                       field.toLowerCase().includes("key") ||
                       field.toLowerCase().includes("token")) && !visibleFields[field] 
                        ? "password" 
                        : "text"
                    }
                    placeholder={field.replace(/([A-Z])/g, " $1")}
                    name={field}
                    value={userData[field]}
                    onChange={handleChange}
                  />
                  {(field.toLowerCase().includes("password") || 
                    field.toLowerCase().includes("secret") || 
                    field.toLowerCase().includes("key") ||
                    field.toLowerCase().includes("token")) && (
                    <button
                      type="button"
                      onClick={() => toggleVisibility(field)}
                      className="absolute right-3 top-1/2 -translate-y-1/2 text-zinc-400 hover:text-zinc-200"
                    >
                      {visibleFields[field] ? (
                        <EyeOff className="h-4 w-4" />
                      ) : (
                        <Eye className="h-4 w-4" />
                      )}
                    </button>
                  )}
                </div>
              </div>
            ))}

          {/* Custom Status Dropdown */}
          <div className="grid grid-cols-1 items-center gap-4">
            <Label className="text-white">Status</Label>
            <Select
              value={userData.status}
              onValueChange={(val) =>
                setUserData((prev) => ({ ...prev, status: val }))
              }
            >
              <SelectTrigger className="text-white text-[15px] hover:bg-zinc-800">
                <SelectValue placeholder="Select Status" />
              </SelectTrigger>
              <SelectContent className="border-zinc-700">
                <SelectGroup>
                  {statusOptions.map((s) => (
                    <SelectItem key={s.value} value={s.value}>
                      {s.label}
                    </SelectItem>
                  ))}
                </SelectGroup>
              </SelectContent>
            </Select>
          </div>

          {/* Product Dropdown */}
          <div className="grid grid-cols-1 items-center gap-4">
            <Label className="text-white">Product</Label>
            <Select
              value={userData.product || "optimus"}
              onValueChange={(val) =>
                setUserData((prev) => ({ ...prev, product: val }))
              }
            >
              <SelectTrigger className="text-white text-[15px] hover:bg-zinc-800">
                <SelectValue placeholder="Select Product" />
              </SelectTrigger>
              <SelectContent className="border-zinc-700">
                <SelectGroup>
                  <SelectItem value="optimus">Optimus</SelectItem>
                  <SelectItem value="multi_asset">Multi-Asset</SelectItem>
                </SelectGroup>
              </SelectContent>
            </Select>
          </div>

          <div className="grid grid-cols-1 items-center gap-4">
            <Label className="text-white">Label</Label>
            <Select
              value={showCustomLabelInput ? "custom" : userData.label}
              onValueChange={(val) => {
                if (val === "custom") {
                  setShowCustomLabelInput(true);
                } else {
                  setShowCustomLabelInput(false);
                  setUserData((prev) => ({ ...prev, label: val }));
                }
              }}
            >
              <SelectTrigger className="text-white text-[15px] hover:bg-zinc-800">
                <SelectValue placeholder="Select Label" />
              </SelectTrigger>
              <SelectContent className="border-zinc-700">
                <SelectGroup>
                  <SelectItem value="clearmind">clearmind</SelectItem>
                  <SelectItem value="satyam">satyam</SelectItem>
                  <SelectItem value="custom">Custom (Type your own)</SelectItem>
                </SelectGroup>
              </SelectContent>
            </Select>
          </div>

          {showCustomLabelInput && (
            <div className="grid grid-cols-1 items-center gap-4">
              <Label className="text-white">Label</Label>
              <Input
                className="text-white mt-2"
                type="text"
                placeholder="Enter custom label"
                name="label"
                value={userData.label}
                onChange={handleChange}
              />
            </div>
          )}
        </div>

        <DialogFooter>
          <Button
            type="submit"
            className="text-[16px] font-normal bg-zinc-800 text-white hover:bg-zinc-700"
            onClick={handleUpdateUser}
          >
            Update User
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
